import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../Home/components/Sidebar';
import Tabs from '../../Home/components/Tabs';
import PortalTemplateItem from './PortalTemplateItem';
import { usePortalBuilderTemplates } from '../hooks/usePortalBuilderTemplates';
import styles from '../styles/PortalBuilderTemplateList.module.css';
import { useAuth } from '../../../contexts/AuthContext';
import {
  fetchPortalBuilderTemplates,
  fetchWorkFlowList,
  deletePortalTemplate,
} from '../services/portalBuilderService';
import { AiOutlinePlus } from 'react-icons/ai';
import Header from '../../../components/common/Header/Header';

const PortalBuilderTemplatesList = () => {
  const { token } = useAuth();
  const [deleteStatus, setDeleteStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('');
  const [templateStatus, setTemplateStatus] = useState('');
  const { templates } = usePortalBuilderTemplates(
    token,
    deleteStatus,
    templateStatus
  );
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState('');
  const [portalBuilderTemplates, setPortalBuilderTemplates] = useState([]);
  const [workflows, setWorkflows] = useState([]);

  const location = useLocation();
  const { delete_status_back, wfw_id, wfw_node_id } = location.state || {};

  const navigate = useNavigate();

  useEffect(() => {
    if (delete_status_back !== undefined) {
      setDeleteStatus(delete_status_back);
    }
  }, [delete_status_back]);

  useEffect(() => {
    setSelectedWorkflow(wfw_id);
    setSelectedNode(wfw_node_id);
  }, [wfw_id, wfw_node_id]);

  useEffect(() => {
    fetchWorkflows();
  }, [templateStatus, deleteStatus]);

  useEffect(() => {
    if (selectedWorkflow) {
      const workflow = workflows.find(
        (w) => String(w.id) === String(selectedWorkflow)
      );
      if (workflow && workflow.workflow?.nodes) {
        setNodes(workflow.workflow.nodes);
      } else {
        setNodes([]);
      }
    } else {
      setNodes([]);
    }
  }, [selectedWorkflow, workflows]);

  const handleDeleteStatusChange = (e) => setDeleteStatus(e.target.value);
  const handleTemplateStatusChange = (e) => setTemplateStatus(e.target.value);
  const handleNodeChange = (e) => {
    setSelectedNode(e.target.value);
  };

  const handleWorkflowChange = (e) => {
    setSelectedWorkflow(e.target.value);
    setSelectedNode('');
  };

  const handlePortalBuilderTemplateClick = (template) => {
    // console.log('invoked handle navigate function');
    navigate(`/portalBuilder/${template.id}`, {
      state: {
        template: template.template,
        template_id: template.id,
        template_name: template.template_name,
        template_json: template.template_json,
        template_status: template.template_status,
        wfw_id: template.wfw_id,
        wfw_node_id: template.wfw_node_id,
        delete_status: template.deleted_at,
      },
    });
  };

  const handleAddTemplate = () => {
    const workflow = workflows.find(
      (w) => String(w.id) === String(selectedWorkflow)
    );
    const workflowName = workflow ? workflow.workflow_name : 'New Template';

    const node = nodes.find((n) => String(n.id) === String(selectedNode));
    const nodeType = node ? node.type : '';
    navigate('/portalBuilder/create', {
      state: {
        template: '',
        template_name: workflowName,
        node_type: nodeType,
        template_id: null,
        wfw_id: selectedWorkflow,
        wfw_node_id: selectedNode,
      },
    });
  };

  const fetchWorkflows = async () => {
    const portalBuilderList = await fetchPortalBuilderTemplates(
      token,
      deleteStatus,
      templateStatus
    );
    const workFlow = await fetchWorkFlowList(token);
    setPortalBuilderTemplates(portalBuilderList);
    setWorkflows(workFlow);
  };

  const filteredTemplates = portalBuilderTemplates.filter((template) => {
    const matchesWorkflow = selectedWorkflow
      ? template.wfw_id === selectedWorkflow
      : true;
    const matchesNode = selectedNode
      ? template.wfw_node_id === selectedNode
      : true;
    return matchesWorkflow && matchesNode;
  });

  const isAddTemplateDisabled = portalBuilderTemplates.some(
    (template) =>
      template.wfw_id === selectedWorkflow &&
      template.wfw_node_id === selectedNode
  );

  return (
    <div className={styles.container}>
      <Header />
      <Sidebar active={'portal-builder-templates'} />
      <div className={styles.content}>
        {/* <Tabs activeTab={activeTab} setActiveTab={setActiveTab} /> */}
        <>
          <div className={styles.filterContainer}>
            <div className={styles.filterParent}>
              <select
                value={selectedWorkflow}
                onChange={handleWorkflowChange}
                style={{ padding: '8px', borderRadius: '4px' }}
              >
                <option value="">All Workflows</option>
                {workflows &&
                  workflows.map((workflow) => (
                    <option key={workflow.id} value={workflow.id}>
                      {workflow.workflow_name}
                    </option>
                  ))}
              </select>

              <select
                value={selectedNode}
                onChange={handleNodeChange}
                style={{ padding: '8px', borderRadius: '4px' }}
                disabled={!selectedWorkflow}
              >
                <option value="">All Nodes</option>
                {nodes.map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.data?.label || `Node ${node.id}`}
                  </option>
                ))}
              </select>

              <select
                value={templateStatus}
                onChange={handleTemplateStatusChange}
                className={styles.filterDropdown}
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="publish">Published</option>
              </select>
              <select
                value={deleteStatus || ''}
                onChange={handleDeleteStatusChange}
                className={styles.filterDropdown}
              >
                <option value="">Available</option>
                <option value="not null">Trashed</option>
              </select>
            </div>
            <button
              style={{
                padding: '8px 16px',
                backgroundColor:
                  isAddTemplateDisabled || !selectedWorkflow || !selectedNode
                    ? '#d3d3d3'
                    : 'var(--primary-color)',
                color:
                  isAddTemplateDisabled || !selectedWorkflow || !selectedNode
                    ? '#888'
                    : '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor:
                  isAddTemplateDisabled || !selectedWorkflow || !selectedNode
                    ? 'not-allowed'
                    : 'pointer',
                marginLeft: 'auto',
              }}
              onClick={handleAddTemplate}
              disabled={
                isAddTemplateDisabled || !selectedWorkflow || !selectedNode
              }
            >
              <AiOutlinePlus className={styles.aiOutline} /> Add Template
            </button>
          </div>

          {filteredTemplates &&
            filteredTemplates.map((template) => {
              const workflow = workflows?.find(
                (w) => String(w.id) === String(template.wfw_id)
              );
              const node = workflow?.workflow?.nodes?.find(
                (n) => String(n.id) === String(template.wfw_node_id)
              );

              return (
                <PortalTemplateItem
                  key={template.id}
                  title={template.template_name}
                  workflowName={workflow?.workflow_name}
                  nodeName={node?.data?.label || `Node ${template.wfw_node_id}`}
                  onClick={() => handlePortalBuilderTemplateClick(template)}
                  deletedAt={template.deleted_at}
                  templateStatus={template.template_status}
                />
              );
            })}
        </>
      </div>
    </div>
  );
};

export default PortalBuilderTemplatesList;
